<script setup lang="ts">
  defineOptions({
    name: 'AnnouncementsTopBarButton',
  })

  import { computed } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Announcement02Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { AnalyticsEvents } from '@common/analytics-layer/types'
  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import CPBadge from '@ui/components/CPBadge.vue'
  import CPButton from '@ui/components/CPButton.vue'

  import { useAnnouncementsUnread } from '../composables/useAnnouncementsUnread'
  import { ANNOUNCEMENT_OPEN_SOURCE } from '../utils/announcement.constants'

  const props = defineProps({
    buttonId: {
      type: String,
      required: true,
    },
    badgeId: {
      type: String,
      required: true,
    },
    buttonClass: {
      type: String,
      default: 'flex-shrink-0 !h-10 !w-10 !min-w-0 !rounded-full !p-0 transition-colors duration-200 hover:!bg-[var(--cp-background-100)]',
    },
  })

  const { t } = useI18n()
  const { unreadCount, isLoggedIn } = useAnnouncementsUnread()
  const { CPNavigateTo } = useCPNavigation()

  const badgeValue = computed(() => {
    return unreadCount.value > 9 ? '9+' : unreadCount.value
  })

  const badgeOffset = computed(() => {
    return unreadCount.value < 10 ? ([0, 0] as [number, number]) : ([2, -2] as [number, number])
  })

  /**
   * Top-bar lives outside NuxtPage. Client SPA nav from here hits Vue
   * `pageTransition` `out-in` (`insertBefore` null) and leaves the previous
   * page mounted. Hard navigate for this entry only — matches reload behaviour
   * without changing global middleware or page meta.
   *
   * Browser history still records the previous URL, so list back navigation works.
   */
  function handleClick(): void {
    void CPNavigateTo({
      name: 'courses-announcements',
    })
  }
</script>

<template>
  <CPButton
    v-if="isLoggedIn"
    :id="props.buttonId"
    v-track="{
      event: AnalyticsEvents.ANNOUNCEMENTS_OPENED,
      source: ANNOUNCEMENT_OPEN_SOURCE.BUTTON,
    }"
    type="ghost"
    size="sm"
    color="transparent"
    :class="props.buttonClass"
    :aria-label="t('announcements.title')"
    @click="handleClick"
  >
    <CPBadge :id="props.badgeId" class="announcements-topbar-badge" :show="unreadCount > 0" :value="badgeValue" :offset="badgeOffset">
      <Announcement02Icon class="h-5 w-5 text-on-sidebar" aria-hidden="true" />
    </CPBadge>
  </CPButton>
</template>

<style scoped>
  .announcements-topbar-badge :deep(.hr-badge-sup) {
    background-color: var(--cp-error-600);
  }

  .announcements-topbar-badge :deep(.hr-badge-sup .hr-base-slot-machine) {
    color: var(--cp-text-on-error) !important;
  }
</style>
